
import React from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";
import { cn } from "@/lib/utils";
import { useSettings } from "@/context/SettingsContext";
import { FileText } from "lucide-react";
import { getRpgThemeClasses } from "@/lib/utils";

const TermsOfService = () => {
  const { darkMode, enableRpgMode } = useSettings();
  const navigate = useNavigate();
  const rpgTheme = getRpgThemeClasses(enableRpgMode, darkMode);

  return (
    <div className={cn(
      "min-h-screen p-4 font-montserrat transition-colors duration-300",
      enableRpgMode ? (
        rpgTheme.background + " rpg-parchment"
      ) : (
        darkMode 
          ? "bg-gradient-to-br from-[#1a0930] to-[#3F2E46] text-[#FBF3F1]" 
          : "bg-gradient-to-br from-[#FBF3F1] to-[#D896A4]/10"
      )
    )}>
      <div className="max-w-4xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <Button 
            variant="ghost" 
            onClick={() => navigate('/')}
            className={cn(
              enableRpgMode ? rpgTheme.text : (darkMode ? "text-[#FBF3F1]" : ""),
              enableRpgMode && "hover:bg-amber-600/20"
            )}
          >
            ← Back to App
          </Button>
        </div>

        <Card className={cn(
          "p-8 shadow-lg border backdrop-blur-sm",
          enableRpgMode ? (
            rpgTheme.cardBackground + " " + rpgTheme.text + " rpg-border"
          ) : (
            darkMode 
              ? "bg-[#2d1a45]/80 border-[#6127A0]/30 text-[#FBF3F1]" 
              : "bg-white/90 border-[#D896A4]/10"
          )
        )}>
          <div className="flex items-center mb-6">
            <FileText className={cn("h-6 w-6 mr-3", enableRpgMode ? "text-amber-500" : (darkMode ? "text-[#00FF99]" : "text-[#6127A0]"))} />
            <h1 className={cn("text-3xl font-bold", enableRpgMode && "drop-shadow-sm")}>
              Terms of Service
            </h1>
          </div>

          <div className="space-y-6 text-sm leading-relaxed">
            <p className="text-xs text-muted-foreground">
              Last updated: {new Date().toLocaleDateString()}
            </p>

            <section>
              <h2 className={cn("text-xl font-semibold mb-3", enableRpgMode && "text-amber-600")}>
                1. Acceptance of Terms
              </h2>
              <p>
                By accessing and using Unstuck ("the Service"), you accept and agree to be bound by the terms and provision of this agreement. If you do not agree to abide by the above, please do not use this service.
              </p>
            </section>

            <section>
              <h2 className={cn("text-xl font-semibold mb-3", enableRpgMode && "text-amber-600")}>
                2. Description of Service
              </h2>
              <p>
                Unstuck is a digital journaling platform that uses dice-driven prompts to help users work through mental blocks and engage in self-reflection. The service provides structured prompts and tools for personal journaling and growth.
              </p>
            </section>

            <section>
              <h2 className={cn("text-xl font-semibold mb-3", enableRpgMode && "text-amber-600")}>
                3. User Accounts
              </h2>
              <p className="mb-3">When you create an account with us, you must provide accurate and complete information. You are responsible for:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Safeguarding your password and account information</li>
                <li>All activities that occur under your account</li>
                <li>Notifying us immediately of any unauthorized use</li>
                <li>Ensuring your contact information remains current</li>
              </ul>
            </section>

            <section>
              <h2 className={cn("text-xl font-semibold mb-3", enableRpgMode && "text-amber-600")}>
                4. Acceptable Use
              </h2>
              <p className="mb-3">You agree to use the Service only for lawful purposes and in accordance with these Terms. You agree NOT to use the Service:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>To violate any applicable local, state, national, or international law</li>
                <li>To harm, threaten, or harass other users</li>
                <li>To share content that is illegal, offensive, or violates intellectual property rights</li>
                <li>To attempt to gain unauthorized access to the Service or other users' accounts</li>
                <li>To use the Service for any commercial purpose without permission</li>
              </ul>
            </section>

            <section>
              <h2 className={cn("text-xl font-semibold mb-3", enableRpgMode && "text-amber-600")}>
                5. Content and Intellectual Property
              </h2>
              <p className="mb-3">
                You retain ownership of all journal content you create. However, by using the Service, you grant us a limited license to store, process, and display your content solely for the purpose of providing the Service.
              </p>
              <p>
                The Service itself, including all software, prompts, and design elements, remains the intellectual property of Unstuck and its creators.
              </p>
            </section>

            <section>
              <h2 className={cn("text-xl font-semibold mb-3", enableRpgMode && "text-amber-600")}>
                6. Privacy and Data Protection
              </h2>
              <p>
                Your privacy is important to us. Please review our Privacy Policy, which also governs your use of the Service, to understand our practices.
              </p>
            </section>

            <section>
              <h2 className={cn("text-xl font-semibold mb-3", enableRpgMode && "text-amber-600")}>
                7. Service Availability
              </h2>
              <p>
                We strive to maintain the Service's availability but cannot guarantee uninterrupted access. We reserve the right to modify, suspend, or discontinue the Service at any time with reasonable notice.
              </p>
            </section>

            <section>
              <h2 className={cn("text-xl font-semibold mb-3", enableRpgMode && "text-amber-600")}>
                8. Limitation of Liability
              </h2>
              <p>
                The Service is provided "as is" without warranties of any kind. We shall not be liable for any indirect, incidental, special, consequential, or punitive damages resulting from your use of the Service.
              </p>
            </section>

            <section>
              <h2 className={cn("text-xl font-semibold mb-3", enableRpgMode && "text-amber-600")}>
                9. Termination
              </h2>
              <p>
                You may terminate your account at any time. We reserve the right to terminate or suspend accounts that violate these Terms of Service.
              </p>
            </section>

            <section>
              <h2 className={cn("text-xl font-semibold mb-3", enableRpgMode && "text-amber-600")}>
                10. Changes to Terms
              </h2>
              <p>
                We reserve the right to modify these terms at any time. We will notify users of significant changes via email or through the Service.
              </p>
            </section>

            <section>
              <h2 className={cn("text-xl font-semibold mb-3", enableRpgMode && "text-amber-600")}>
                11. Contact Information
              </h2>
              <p>
                If you have any questions about these Terms of Service, please contact us at{" "}
                <a 
                  href="mailto:hello@ameliastewart.co" 
                  className={cn(
                    "hover:underline",
                    enableRpgMode ? "text-amber-500 hover:text-amber-400" : (
                      darkMode ? "text-[#AE78F0] hover:text-[#c096ff]" : "text-[#6127A0] hover:text-[#4d1f80]"
                    )
                  )}
                >
                  hello@ameliastewart.co
                </a>
              </p>
            </section>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default TermsOfService;
